import { ChevronRight, Eye, Info, RotateCcw, UserX } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusPill } from "@/components/status-pill";
import { resolveBilingual, type ResolvedText } from "@/lib/bilingual";
import {
  CLIENT_INFO_PLACEHOLDER,
  EMPTY_CLIENT_INFO,
  isClientInfoComplete,
  type ClientInfoValue,
} from "@/lib/clientInfo";
import type { LanguagePreference } from "@/types/api";
import type { BuilderEdge, BuilderNode } from "@/components/flow-builder/ScriptOutline";

/** Preview-only stand-in for FlowPlayerPage's FreeTextAnswer — nothing
 * here is persisted (this panel never writes a CallLog), so it doesn't
 * need to track or submit what's typed, just require *something* before
 * Continue enables, matching what Staff will actually see. */
function PreviewFreeTextAnswer({ onContinue }: { onContinue: () => void }) {
  const [value, setValue] = useState("");
  return (
    <div className="space-y-2">
      <Textarea
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Type what the customer said…"
        maxLength={500}
        className="text-sm"
      />
      <Button size="sm" onClick={onContinue} disabled={!value.trim()}>
        Continue
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
}

/** Preview-only stand-in for FlowPlayerPage's ClientInfoAnswer — same
 * required fields (name, last name, DOB, phone; email optional) and the
 * same "client declined" placeholder button, just never submitted
 * anywhere, matching this panel's "nothing here is saved" contract. */
function PreviewClientInfoAnswer({ onContinue }: { onContinue: () => void }) {
  const [value, setValue] = useState<ClientInfoValue>(EMPTY_CLIENT_INFO);
  const set = (patch: Partial<ClientInfoValue>) => setValue((v) => ({ ...v, ...patch }));

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={value.first_name}
          onChange={(e) => set({ first_name: e.target.value })}
          placeholder="First name"
          className="text-sm"
          maxLength={150}
        />
        <Input
          value={value.last_name}
          onChange={(e) => set({ last_name: e.target.value })}
          placeholder="Last name"
          className="text-sm"
          maxLength={150}
        />
      </div>
      <div>
        <Label className="text-[10px] text-slate-500">Date of birth</Label>
        <Input
          type="date"
          value={value.date_of_birth}
          onChange={(e) => set({ date_of_birth: e.target.value })}
          className="text-sm"
        />
      </div>
      <Input
        type="email"
        value={value.email}
        onChange={(e) => set({ email: e.target.value })}
        placeholder="Email (optional)"
        className="text-sm"
        maxLength={254}
      />
      <Input
        type="tel"
        value={value.phone_number}
        onChange={(e) => set({ phone_number: e.target.value })}
        placeholder="Phone number"
        className="text-sm"
        maxLength={30}
      />
      {/* Both real buttons, side by side — see FlowPlayerPage's
       * ClientInfoAnswer for why a plain text link here read as attached
       * to (or gated behind) Continue's arrow. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" onClick={() => setValue(CLIENT_INFO_PLACEHOLDER)}>
          <UserX className="h-3.5 w-3.5" aria-hidden="true" />
          Client declined — use placeholder
        </Button>
        <Button size="sm" onClick={onContinue} disabled={!isClientInfoComplete(value)}>
          Continue
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

/** Compact bilingual renderer for the preview sidebar — same idea as the
 * one on FlowPlayerPage (both languages shown directly, never hidden
 * behind a hover), just sized down to fit a narrow column. */
function PreviewBilingualBlock({ text, className }: { text: ResolvedText; className: string }) {
  if (!text.secondary) {
    return <p className={className}>{text.primary}</p>;
  }
  return (
    <div className="space-y-2">
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">EN</span>
        <p className={className}>{text.primary}</p>
      </div>
      <div className="border-t border-slate-200 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">ES</span>
        <p className={className}>{text.secondary}</p>
      </div>
    </div>
  );
}

function firstRealNodeId(nodes: BuilderNode[], edges: BuilderEdge[]): string | null {
  const start = nodes.find((n) => n.node_type === "START");
  if (!start) return null;
  const startEdges = edges.filter((e) => e.source === start.id);
  // A plain pass-through START (exactly one outgoing edge) skips straight
  // to the real first step. A START that itself branches — e.g. a
  // Selection step saved as the flow's first step — must stay put so the
  // preview shows all of its options, not just the first one. See the
  // matching fix in FlowPlayerPage.tsx's firstRealNodeId.
  return startEdges.length === 1 ? startEdges[0].target : start.id;
}

interface FlowPreviewPanelProps {
  flowTitle: string;
  nodes: BuilderNode[];
  edges: BuilderEdge[];
}

/**
 * Live, read-only simulation of what Staff see in FlowPlayerPage — driven
 * straight off the builder's in-memory nodes/edges (not the saved graph),
 * so admins can click through the script exactly as it currently is —
 * including unsaved edits — without saving, publishing, or logging in as
 * a test Staff account. Mount with `key={flow.id}` so switching scripts
 * resets the walkthrough instead of pointing at a stale node id.
 */
export function FlowPreviewPanel({ flowTitle, nodes, edges }: FlowPreviewPanelProps) {
  const [languagePreference, setLanguagePreference] = useState<LanguagePreference>("BOTH");
  const startId = firstRealNodeId(nodes, edges);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(startId);
  const [breadcrumb, setBreadcrumb] = useState<string[]>([]);

  const nodesById = new Map(nodes.map((n) => [n.id, n]));

  // Re-anchor on the first step whenever the node the preview is sitting on
  // is no longer valid — the admin deleted the step being previewed, or
  // rewired the first step — *and* whenever it was never valid to begin
  // with, e.g. the flow started out empty (currentNodeId stuck at null)
  // and the admin has since added the first step.
  useEffect(() => {
    const isValid = currentNodeId !== null && nodesById.has(currentNodeId);
    if (!isValid && currentNodeId !== startId) {
      setCurrentNodeId(startId);
      setBreadcrumb([]);
    }
  }, [currentNodeId, nodesById, startId]);

  const currentNode = currentNodeId ? nodesById.get(currentNodeId) ?? null : null;
  const outgoingEdges = currentNode
    ? edges.filter((e) => e.source === currentNode.id).sort((a, b) => a.sort_order - b.sort_order)
    : [];
  const isEnd = currentNode?.node_type === "END";

  const restart = () => {
    setBreadcrumb([]);
    setCurrentNodeId(startId);
  };

  const choose = (edge: BuilderEdge) => {
    if (!currentNodeId) return;
    setBreadcrumb((b) => [...b, currentNodeId]);
    setCurrentNodeId(edge.target);
  };

  const goBack = () => {
    setBreadcrumb((b) => {
      if (b.length === 0) return b;
      const prev = b[b.length - 1];
      setCurrentNodeId(prev);
      return b.slice(0, -1);
    });
  };

  const spiel = currentNode
    ? resolveBilingual(languagePreference, {
        en: currentNode.spiel_text || currentNode.title,
        es: currentNode.spiel_text_es || currentNode.title_es,
      })
    : null;
  const internalNote = currentNode
    ? resolveBilingual(languagePreference, {
        en: currentNode.internal_note,
        es: currentNode.internal_note_es,
      })
    : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Eye className="h-4 w-4 text-primary" aria-hidden="true" />
          Live preview
        </div>
        <Select
          value={languagePreference}
          onValueChange={(v) => setLanguagePreference(v as LanguagePreference)}
        >
          <SelectTrigger
            className="h-7 w-auto gap-1 border-none bg-transparent px-1.5 text-xs text-slate-500 hover:bg-surface-alt"
            aria-label="Preview language"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="EN">English</SelectItem>
            <SelectItem value="ES">Spanish</SelectItem>
            <SelectItem value="BOTH">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="shrink-0 border-b border-slate-200 bg-surface-alt px-4 py-2 text-xs text-slate-500">
        What Staff sees on this step — nothing here is saved.
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {!startId ? (
          <div className="rounded-md border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
            Add a first step to see the preview.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <h2 className="m-0 truncate text-sm font-semibold text-slate-900">{flowTitle}</h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={restart}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Restart
              </Button>
            </div>

            {breadcrumb.length > 0 && (
              <nav className="mt-2 flex flex-wrap items-center gap-1 text-xs text-slate-400">
                {breadcrumb.map((id) => (
                  <span key={id} className="flex items-center gap-1">
                    <span className="max-w-[6rem] truncate">
                      {nodesById.get(id)?.title || "Untitled step"}
                    </span>
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                  </span>
                ))}
                <span className="max-w-[8rem] truncate font-medium text-slate-600">
                  {currentNode?.title || "Untitled step"}
                </span>
              </nav>
            )}

            <div className="app-card mt-3 p-4">
              {isEnd ? (
                <div className="text-center">
                  <StatusPill kind="success">Flow complete</StatusPill>
                </div>
              ) : (
                <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {currentNode?.builder_step_type === "text_entry"
                    ? "Type the answer"
                    : currentNode?.builder_step_type === "client_info"
                      ? "Client information"
                      : currentNode?.node_type === "DECISION"
                        ? "Decision point"
                        : "Say this"}
                </div>
              )}

              {spiel && (
                <div className="mt-2">
                  <PreviewBilingualBlock
                    text={spiel}
                    className="text-sm leading-relaxed text-slate-900"
                  />
                  {spiel.isFallback && (
                    <p className="mt-1 text-xs italic text-slate-400">
                      Spanish not available — showing English.
                    </p>
                  )}
                </div>
              )}

              {internalNote && internalNote.primary && (
                <div className="mt-3 flex gap-2 rounded-md border border-warning/30 bg-warning-subtle p-2.5 text-xs text-slate-800">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
                  <div>
                    <div className="font-medium text-warning">Note for the agent</div>
                    <PreviewBilingualBlock text={internalNote} className="mt-0.5 leading-relaxed" />
                  </div>
                </div>
              )}
            </div>

            {!isEnd && currentNode && currentNode.builder_step_type === "text_entry" && (
              <div className="mt-3">
                {outgoingEdges[0] ? (
                  <PreviewFreeTextAnswer onContinue={() => choose(outgoingEdges[0])} />
                ) : (
                  <p className="text-xs text-danger">No branch configured yet from this step.</p>
                )}
              </div>
            )}

            {!isEnd && currentNode && currentNode.builder_step_type === "client_info" && (
              <div className="mt-3">
                {outgoingEdges[0] ? (
                  <PreviewClientInfoAnswer onContinue={() => choose(outgoingEdges[0])} />
                ) : (
                  <p className="text-xs text-danger">No branch configured yet from this step.</p>
                )}
              </div>
            )}

            {!isEnd &&
              currentNode &&
              currentNode.builder_step_type !== "text_entry" &&
              currentNode.builder_step_type !== "client_info" && (
              <div className="mt-3 space-y-1.5">
                {outgoingEdges.length === 0 ? (
                  <p className="text-xs text-danger">No branch configured yet from this step.</p>
                ) : (
                  outgoingEdges.map((edge) => {
                    const label = resolveBilingual(languagePreference, {
                      en: edge.label || "Continue",
                      es: edge.label_es,
                    });
                    return (
                      <button
                        key={edge.id}
                        type="button"
                        onClick={() => choose(edge)}
                        className="app-focus-ring flex w-full cursor-pointer items-center justify-between rounded-md border border-slate-300 bg-surface px-3 py-2 text-left text-sm font-medium text-slate-900 transition-colors duration-150 hover:border-primary hover:bg-primary-subtle"
                      >
                        <span>
                          {label.primary}
                          {label.secondary && (
                            <span className="block text-xs font-normal text-slate-500">
                              {label.secondary}
                            </span>
                          )}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {breadcrumb.length > 0 && !isEnd && (
              <button
                type="button"
                onClick={goBack}
                className="mt-3 cursor-pointer text-xs text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
              >
                ← Back
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
